/**
 * LoginPage — step 1 of authentication.
 *
 * UX:
 *   - User enters Iranian mobile number
 *   - We validate locally (libphonenumber-js via @/utils/phone)
 *   - On submit: call authApi.requestOtp(phoneE164)
 *   - On success: navigate to /otp with the phone in router state
 *   - On 429 (RESEND_TOO_SOON): show how long to wait
 *   - Errors are surfaced inline (no toast system yet)
 *
 * Accessibility / mobile notes:
 *   - inputMode="tel" + autoComplete="tel" → numeric keyboard on mobile
 *   - dir="ltr" on the phone field so digits read LTR even in Persian RTL UI
 *   - The form does NOT submit on Enter while loading (button disabled)
 *
 * Backend contract: POST /auth/request-otp
 *   Body:     { phone: "+989..." }
 *   Success:  { data: { expiresIn: 120, resendAvailableIn: 60 } }
 *   Errors:   400 INVALID_PHONE, 429 RESEND_TOO_SOON (details.retryAfter)
 *   See:      BACKEND_CONTRACT.md
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Container,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';

import LanguageToggle from '@/components/LanguageToggle';
import { authApi, isMockMode } from '@/services/api';
import { isValidIranianMobile, toE164 } from '@/utils/phone';

/**
 * Zod schema — uses libphonenumber-js to validate Iranian mobiles.
 * We DON'T use a regex here because libphonenumber correctly handles
 * the many formats Iranian users type (09xxx, +989xxx, with/without spaces).
 */
const buildSchema = (t) =>
  z.object({
    phone: z
      .string()
      .min(1, t('auth.phoneInvalid'))
      .refine(isValidIranianMobile, t('auth.phoneInvalid')),
  });

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [serverError, setServerError] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(buildSchema(t)),
    defaultValues: { phone: '' },
    mode: 'onBlur',
  });

  /**
   * Form submit handler.
   * @param {{ phone: string }} values
   */
  const onSubmit = async (values) => {
    setServerError(null);
    const phoneE164 = toE164(values.phone);
    if (!phoneE164) {
      setServerError(t('auth.phoneInvalid'));
      return;
    }

    try {
      const { resendAvailableIn, expiresIn } = await authApi.requestOtp(phoneE164);
      // Hand off to the OTP page. The phone is the source of truth
      // for the verify step; we pass timing hints too.
      navigate('/otp', {
        replace: true,
        state: {
          phone: phoneE164,
          resendAvailableIn: resendAvailableIn ?? 60,
          expiresIn: expiresIn ?? 120,
        },
      });
    } catch (err) {
      handleApiError(err);
    }
  };

  /**
   * Map API errors to user-facing messages.
   * @param {any} err
   */
  const handleApiError = (err) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.error?.code;
    const details = err?.response?.data?.error?.details;

    if (status === 429 && code === 'RESEND_TOO_SOON') {
      setServerError(
        t('auth.rateLimited', { seconds: details?.retryAfter ?? 60 })
      );
      return;
    }
    if (status === 400) {
      setServerError(t('auth.phoneInvalid'));
      return;
    }
    if (!err?.response) {
      // No HTTP response object → most likely a network failure.
      setServerError(t('auth.networkError'));
      return;
    }
    setServerError(t('errors.serverError'));
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2,
      background: 'linear-gradient(160deg,#11223E 0%,#0A1425 100%)' }}>
      <Box sx={{ position: 'absolute', top: 12, insetInlineEnd: 12, color: '#fff' }}>
        <LanguageToggle />
      </Box>
      <Container maxWidth="xs" disableGutters sx={{ width: '100%' }}>
        <Paper elevation={8} sx={{ p: 4, borderRadius: 4 }}>
          <Box textAlign="center" sx={{ mb: 3 }}>
            <PhoneIphoneIcon sx={{ fontSize: 56, color: 'primary.main', mb: 1 }} />
            <Typography variant="h5" component="h1" fontWeight={800}>
              {t('auth.loginTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t('auth.loginSubtitle')}
            </Typography>
          </Box>
          {/* Note: <form> tag is fine here (not inside an artifact) */}
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <Stack spacing={2}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('auth.phoneLabel')}
                    placeholder={t('auth.phonePlaceholder')}
                    fullWidth
                    autoFocus
                    autoComplete="tel"
                    inputMode="tel"
                    error={!!errors.phone}
                    helperText={errors.phone?.message ?? ' '}
                    // Phone numbers are always read LTR even in RTL UIs.
                    inputProps={{ dir: 'ltr', style: { textAlign: 'left' } }}
                    disabled={isSubmitting}
                  />
                )}
              />

              {serverError && (
                <Alert severity="error" onClose={() => setServerError(null)}>
                  {serverError}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isSubmitting}
                fullWidth
              >
                {isSubmitting ? t('auth.sendingOtp') : t('auth.sendOtp')}
              </Button>

              {isMockMode && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  {t('auth.mockHint')}
                </Typography>
              )}
            </Stack>
          </form>
        </Paper>
      </Container>
    </Box>
  );
}

export default LoginPage;
